import { useState, type FormEvent } from "react";
import { api, ApiError } from "../../data/api";
import type { SessionMarker } from "../../data/session";
import { Button, Logo } from "../../ui";

interface MeResponse {
  id: string;
  username: string;
  displayName: string;
  kind: string;
}

export interface LoginPageProps {
  onLoggedIn: (marker: SessionMarker) => void;
  /** Shown when the session expired: local data and unsynced changes are kept. */
  expired?: boolean;
}

export function LoginPage({ onLoggedIn, expired }: LoginPageProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const me = await api<MeResponse>("/auth/login", {
        method: "POST",
        body: { username, password },
      });
      onLoggedIn({
        userId: me.id,
        username: me.username,
        displayName: me.displayName,
        kind: me.kind,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't log in");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-[420px] flex-col justify-center gap-[22px] p-7">
      <div className="flex flex-col items-center gap-2.5">
        <Logo size={56} />
        <h1 className="m-0 text-[26px] font-extrabold">Budgeter</h1>
      </div>
      {expired && (
        <p
          className="rounded-[14px] border border-low-bg bg-low-bg/50 p-3 text-[14px] text-low"
          role="status"
        >
          Your session has ended. Log in again — nothing on this device has been lost, and unsynced
          changes will be sent afterwards.
        </p>
      )}
      <form className="flex flex-col gap-3" onSubmit={submit}>
        <label htmlFor="username" className="text-[14px] font-bold">
          Username
        </label>
        <input
          id="username"
          className="h-12 rounded-xl border border-line bg-surface px-3.5 text-[16px]"
          autoComplete="username"
          autoCapitalize="none"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        />
        <label htmlFor="password" className="text-[14px] font-bold">
          Password
        </label>
        <input
          id="password"
          type="password"
          className="h-12 rounded-xl border border-line bg-surface px-3.5 text-[16px]"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error && (
          <p role="alert" className="m-0 text-[14px] font-semibold text-neg">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" size="lg" className="mt-1.5" disabled={busy}>
          {busy ? "Logging in…" : "Log in"}
        </Button>
      </form>
      <p className="m-0 text-center text-[13px] leading-normal text-muted">
        You'll stay logged in on this device for 90 days.
        <br />
        Forgotten your password? Reset it with the server command.
      </p>
      <div className="rounded-[14px] border border-line bg-surface px-3.5 py-3 text-[13px] leading-[1.45] text-muted">
        <b className="text-ink">Install it:</b> in Chrome, Menu → Add to Home screen. On iPhone,
        Share → Add to Home Screen. After that it opens like an app and works offline.
      </div>
    </main>
  );
}
